import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi } from "../lib/api";
import ListingCard from "../components/ListingCard";
import Breadcrumb from "../components/Breadcrumb";
import { ArrowLeft, Loader2 } from "lucide-react";

const Loading = () => (
  <div className="pt-40 min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#12964A]" /></div>
);

const resolveErrorMessage = (err) => {
  if (err?.response?.status === 404) return "Pagina non trovata";
  return err?.response?.data?.detail || "Errore nel caricamento.";
};

const Subcategory = () => {
  const { catSlug, subSlug } = useParams();
  const [category, setCategory] = useState(null);
  const [sub, setSub] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    Promise.all([
      publicApi.getCategory(catSlug),
      publicApi.getSubcategory(catSlug, subSlug),
      publicApi.listBusinesses({ category: catSlug, subcategory: subSlug, limit: 200, order: "name" }),
    ])
      .then(([c, s, b]) => {
        if (cancelled) return;
        setCategory(c.data); setSub(s.data);
        setBusinesses(b.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("Subcategory load failed", err);
        setError(resolveErrorMessage(err));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [catSlug, subSlug]);

  if (loading) return <Loading />;
  if (error || !category || !sub) {
    return (
      <div className="pt-40 min-h-[60vh] px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="font-display font-bold text-4xl text-[#1F2937]">{error || "Pagina non trovata"}</h1>
          <Link to={`/categoria/${catSlug}`} className="inline-flex items-center gap-2 mt-8 text-[#12964A] hover:underline">
            <ArrowLeft className="w-4 h-4" /> Torna alla categoria
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32">
      <section className="pb-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Breadcrumb items={[
            { label: "Home", to: "/" },
            { label: "Attività", to: "/attivita" },
            { label: category.name, to: `/categoria/${category.slug}` },
            { label: sub.name },
          ]} />

          <div className="mt-6">
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-4">{category.name} · Sottocategoria</div>
            <h1 className="font-display font-bold text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#1F2937] max-w-4xl">{sub.name}</h1>
            {sub.description && (
              <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl">{sub.description}</p>
            )}
            <p className="mt-6 text-sm text-slate-500">
              <strong className="text-[#12964A]">{businesses.length}</strong> attività in questa sottocategoria
            </p>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {businesses.length === 0 ? (
            <div className="py-16 text-center">
              <p className="font-display text-2xl text-[#1F2937]">Nessuna attività in questa sottocategoria.</p>
              <p className="text-slate-500 mt-3">Contenuti in aggiornamento.</p>
              <Link to={`/categoria/${category.slug}`} className="inline-flex items-center gap-2 mt-8 text-[#12964A] hover:underline">
                Vedi tutte le attività di {category.name}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((b) => <ListingCard key={b.id || b.slug} listing={b} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Subcategory;
